import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { LANDING_COLOR_DEFAULTS } from "@/constants/landing";
import type { LandingSettings } from "@/context/CampaignBuilderContext";

interface CustomColorsControlsProps {
  settings: LandingSettings;
  onChange: (v: Partial<LandingSettings>) => void;
}

/** One "swatch — label — hex — reset" row of the Custom Colors panel. */
function ColorPickerRow({
  label,
  value,
  defaultValue,
  onChange,
}: {
  label: string;
  value: string;
  defaultValue: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-3 mb-3 last:mb-0">
      <input
        type="color"
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="gfg-color-input appearance-none w-9 h-9 shrink-0 border-2 border-smoke rounded-lg p-0.5 cursor-pointer bg-transparent transition-colors hover:border-dark-dust focus:border-rodeo-black focus:outline-none"
      />
      <span className="flex-1 font-lato text-[13px] text-rodeo-black">{label}</span>
      <span className="font-lato text-[12px] uppercase text-graphite tracking-[0.5px]">
        {value}
      </span>
      <button
        type="button"
        onClick={() => onChange(defaultValue)}
        title="Reset to default"
        aria-label={`Reset ${label} to default`}
        className="flex items-center justify-center w-7 h-7 rounded-md text-graphite hover:text-rodeo-black hover:bg-wax transition-colors cursor-pointer"
      >
        <RestartAltIcon sx={{ fontSize: 16 }} />
      </button>
    </div>
  );
}

/**
 * Custom Colors body (Pro) for the Landing → Branding tab: a Hero Text dark/light
 * toggle + Hero Background / Gift Message Envelope / Accent color pickers. Accent is
 * the dust brand token, applied to the preview by recoloring `--color-dark-dust`.
 */
export default function CustomColorsControls({ settings, onChange }: CustomColorsControlsProps) {
  const light = settings.heroTextMode === "light";

  const modePill = (active: boolean) =>
    [
      "flex flex-1 items-center justify-center gap-1.5 px-3 py-2 rounded-md border cursor-pointer transition-all",
      "font-lato text-[12px] font-semibold",
      active
        ? "border-dark-dust bg-clear-dust text-dark-dust"
        : "border-smoke bg-white text-graphite hover:border-dark-dust",
    ].join(" ");

  return (
    <div>
      {/* Hero Text — dark vs light for a dark hero background */}
      <div className="mb-4">
        <span className="font-lato text-[11px] font-bold uppercase tracking-[0.5px] text-graphite block mb-2">
          Hero Text
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            className={modePill(!light)}
            onClick={() => onChange({ heroTextMode: "dark" })}
          >
            <DarkModeOutlinedIcon sx={{ fontSize: 15 }} />
            Dark
          </button>
          <button
            type="button"
            className={modePill(light)}
            onClick={() => onChange({ heroTextMode: "light" })}
          >
            <LightModeOutlinedIcon sx={{ fontSize: 15 }} />
            Light
          </button>
        </div>
      </div>

      <ColorPickerRow
        label="Hero Background"
        value={settings.heroBgColor}
        defaultValue={LANDING_COLOR_DEFAULTS.heroBg}
        onChange={(v) => onChange({ heroBgColor: v })}
      />
      <ColorPickerRow
        label="Gift Message Envelope"
        value={settings.envelopeColor}
        defaultValue={LANDING_COLOR_DEFAULTS.envelope}
        onChange={(v) => onChange({ envelopeColor: v })}
      />
      <ColorPickerRow
        label="Accent"
        value={settings.accentColor}
        defaultValue={LANDING_COLOR_DEFAULTS.accent}
        onChange={(v) => onChange({ accentColor: v })}
      />
    </div>
  );
}
